/** Polls /v1/signatures/<id> + renders the per-signatory status.
 *
 * One row per signatory with a status pill, a "Open signature link"
 * link to the magic URL DataSure returned, and a green check when the
 * status flips to signed. When the transaction reaches a terminal
 * state with `signed_pdf_evidence_id`, a "Download signed PDF" button
 * appears.
 */
import { useMutation, useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { ApiPaths, api } from "@/lib/api";
import type {
  SignatoryStatus,
  SignatureTransaction,
  SignatureTransactionStatus,
} from "@/lib/types";

import { fetchSignature } from "./api";


const TERMINAL: Set<SignatureTransactionStatus> = new Set([
  "completed",
  "cancelled",
  "refused",
  "failed",
]);


function statusTone(status: SignatoryStatus | SignatureTransactionStatus): string {
  switch (status) {
    case "signed":
    case "completed":
      return "bg-cvp-ok/20 text-cvp-ok";
    case "refused":
    case "cancelled":
    case "failed":
      return "bg-cvp-danger/20 text-cvp-danger";
    case "sent":
    case "created":
    case "pending":
      return "bg-cvp-warn/20 text-cvp-warn";
    default:
      return "bg-nb-surface text-nb-text-2";
  }
}


export function SignatureStatusCard({
  signatureId,
  onDismiss,
}: {
  signatureId: string;
  onDismiss?: () => void;
}) {
  const { t } = useTranslation();

  const txn = useQuery({
    queryKey: ["signature-transaction", signatureId],
    queryFn: () => fetchSignature(signatureId),
    refetchInterval: (q) => {
      const data = q.state.data as SignatureTransaction | undefined;
      if (!data) return 2000;
      return TERMINAL.has(data.status) ? false : 2000;
    },
  });

  const downloadSigned = useMutation({
    mutationFn: async () => {
      // Dev/test backend returns {url: mock-s3://...}; prod returns 302.
      try {
        const res = await api.get<{ url?: string }>(
          ApiPaths.signatureSignedPdf(signatureId),
          { validateStatus: (s) => s === 200 || s === 302 },
        );
        if (res.data?.url) {
          window.open(res.data.url, "_blank");
          return;
        }
      } catch {
        // Fall through to direct-window-open below — prod 302 path
        // throws on the axios layer.
      }
      window.open(ApiPaths.signatureSignedPdf(signatureId), "_blank");
    },
  });

  if (txn.isLoading || !txn.data) {
    return (
      <div className="card border-nb-border bg-nb-surface/40 text-xs text-nb-text-2">
        {t("signature.status.loading", "Loading signature status…")}
      </div>
    );
  }

  const t_data = txn.data;
  const terminal = TERMINAL.has(t_data.status);

  return (
    <div className="card border-nb-border bg-nb-surface/40 space-y-2 text-sm">
      <header className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span
            className={`rounded px-1.5 py-0.5 text-[10px] uppercase ${statusTone(
              t_data.status,
            )}`}
          >
            {t(`signature.status.${t_data.status}`, t_data.status)}
          </span>
          <span className="text-xs text-nb-text-3">
            {t_data.signatories.length}{" "}
            {t("signature.status.signersWord", "signers")}
          </span>
        </div>
        {onDismiss && terminal && (
          <button
            type="button"
            className="text-xs text-nb-text-3 hover:underline"
            onClick={onDismiss}
          >
            {t("common.dismiss", "Dismiss")}
          </button>
        )}
      </header>

      <ul className="divide-y divide-nb-border/40 text-xs">
        {t_data.signatories.map((s) => (
          <li key={s.id} className="flex items-center justify-between gap-2 py-1.5">
            <div className="min-w-0 flex-1 truncate">
              <span className="font-medium text-nb-text">
                #{s.order} {s.first_name} {s.last_name}
              </span>
              <span className="ml-1 text-nb-text-3">{s.email}</span>
            </div>
            <span
              className={`rounded px-1.5 py-0.5 text-[10px] uppercase ${statusTone(
                s.status,
              )}`}
            >
              {t(`signature.signatoryStatus.${s.status}`, s.status)}
            </span>
            {s.signatory_url && !["signed", "refused"].includes(s.status) && (
              <a
                href={s.signatory_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[11px] text-nb-mint hover:underline"
              >
                {t("signature.status.openLink", "Open link")}
              </a>
            )}
          </li>
        ))}
      </ul>

      {t_data.fatal_error && (
        <p className="text-xs text-cvp-danger">{t_data.fatal_error}</p>
      )}

      {t_data.status === "completed" && t_data.signed_pdf_evidence_id && (
        <button
          type="button"
          className="btn-primary text-xs"
          onClick={() => downloadSigned.mutate()}
        >
          {t("signature.status.downloadSigned", "Download signed PDF")}
        </button>
      )}
    </div>
  );
}
